import { Controller } from "@hotwired/stimulus"

// Persists Bootstrap tab selection across page reloads using localStorage
//
// Usage with anchor tags:
//   <ul class="nav nav-tabs" data-controller="tab-persistence" data-tab-persistence-key-value="my-unique-key">
//     <a class="nav-link" data-bs-toggle="tab" href="#tab1" data-tab-persistence-target="tab">Tab 1</a>
//     <a class="nav-link" data-bs-toggle="tab" href="#tab2" data-tab-persistence-target="tab">Tab 2</a>
//   </ul>
//
// Usage with button tags:
//   <ul class="nav nav-tabs" data-controller="tab-persistence" data-tab-persistence-key-value="my-unique-key">
//     <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tab1" data-tab-persistence-target="tab">Tab 1</button>
//     <button class="nav-link" data-bs-toggle="tab" data-bs-target="#tab2" data-tab-persistence-target="tab">Tab 2</button>
//   </ul>
//
// To exclude a tab from persistence (e.g., one-time views like engagement letters):
//   <button class="nav-link" data-bs-toggle="tab" data-bs-target="#engagement"
//           data-tab-persistence-target="tab" data-tab-persistence-skip>Engagement</button>
//
export default class extends Controller {
  static targets = ["tab"]
  static values = {
    key: String  // Unique localStorage key for this set of tabs
  }

  connect() {
    // Restore previously selected tab from localStorage
    this.restoreTab()

    // Save tab selection when changed
    this.element.addEventListener('shown.bs.tab', this.saveTab.bind(this))
  }

  // A link to a tab's anchor (e.g. a breadcrumb to /opportunities/1#transcripts)
  // opens that tab over the saved one.
  restoreTab() {
    const hash = window.location.hash
    const linked = hash && this.tabTargets.some(tab =>
      tab.getAttribute('href') === hash || tab.getAttribute('data-bs-target') === hash
    )
    const savedTarget = linked ? hash : localStorage.getItem(this.keyValue)

    if (savedTarget) {
      // Find the matching tab target
      const tabTrigger = this.tabTargets.find(tab => {
        const href = tab.getAttribute('href')
        const bsTarget = tab.getAttribute('data-bs-target')
        return href === savedTarget || bsTarget === savedTarget
      })

      // Don't restore tabs that are marked to skip persistence
      // (e.g., engagement letter should not be restored after signing)
      if (tabTrigger && !tabTrigger.hasAttribute('data-tab-persistence-skip')) {
        const tab = new bootstrap.Tab(tabTrigger)
        tab.show()
      }
    }
  }

  saveTab(event) {
    // Don't save tabs that are marked to skip persistence
    if (event.target.hasAttribute('data-tab-persistence-skip')) {
      return
    }

    // Get target from href (anchor) or data-bs-target (button)
    const target = event.target.getAttribute('href') ||
                   event.target.getAttribute('data-bs-target')

    if (target) {
      localStorage.setItem(this.keyValue, target)
    }
  }
}

